import { SendOutlined } from "@ant-design/icons"
import { Button, Form, Input, Select, Typography } from "antd"
import { useCallback, useEffect, useState } from "react"

import Flex from "#blocks/Flex"
import CheckboxWrapper from "#blocks/Form/CheckboxWrapper"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import {
  HELP_REQUEST_PRIORITY,
  HelpRequestRoom,
} from "#customTypes/composedTypes/helpRequestTypes"

import { HELP_REQUEST_ROOM_OPTIONS } from "./helpRequestConstants"
import { useHelpRequestContext } from "./HelpRequestContextProvider"
import { useCurrentUserRoom } from "./useCurrentUserRoom"

const { Text } = Typography

interface FormValues {
  room: HelpRequestRoom
  urgent: boolean
  comment?: string
}

const HelpRequestForm = () => {
  const { createHelpRequest } = useHelpRequestContext()
  const currentRoom = useCurrentUserRoom()
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm<FormValues>()
  const theme = useThemeContext()

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)
      const priority = values.urgent
        ? HELP_REQUEST_PRIORITY.URGENT
        : HELP_REQUEST_PRIORITY.LOW
      await createHelpRequest(values.room, priority, values.comment)
      form.resetFields()
      if (currentRoom) {
        form.setFieldValue("room", currentRoom)
      }
    } finally {
      setLoading(false)
    }
  }, [createHelpRequest, form, currentRoom])

  useEffect(() => {
    if (currentRoom) {
      form.setFieldValue("room", currentRoom)
    }
  }, [currentRoom, form])

  return (
    <Flex col style={{ flexShrink: 0 }}>
      <Text
        type="secondary"
        strong
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          display: "block",
          marginBottom: 6,
        }}
      >
        New Request
      </Text>
      <Form form={form} layout="vertical" initialValues={{ urgent: false }}>
        <Form.Item
          name="room"
          label="Room"
          rules={[{ required: true, message: "Select a room" }]}
          style={{ marginBottom: 8 }}
        >
          <Select
            options={HELP_REQUEST_ROOM_OPTIONS}
            placeholder="Select room"
            showSearch
            optionFilterProp="label"
            size="small"
          />
        </Form.Item>
        <CheckboxWrapper
          name="urgent"
          label={
            <Text type="danger" strong style={{ fontSize: 12 }}>
              Urgent
            </Text>
          }
        />
        <Form.Item name="comment" label="Comment" style={{ marginBottom: 30 }}>
          <Input.TextArea
            rows={2}
            placeholder='e.g. "Need 4 pencils"'
            maxLength={200}
            showCount
            size="small"
          />
        </Form.Item>
        <Button
          type="primary"
          style={{ backgroundColor: theme.success }}
          icon={<SendOutlined />}
          onClick={handleSubmit}
          loading={loading}
          block
          size="small"
        >
          Send
        </Button>
      </Form>
    </Flex>
  )
}

export default HelpRequestForm
